import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Grid,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Send as SendIcon,
  CloudUpload as UploadIcon,
  AttachFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { taskApi, documentApi, commentApi } from '../api/services';
import type { Comment } from '../api/services';
import { useCryptoKeyStore, useEncryption, isEncryptedProcessVariable } from '../features/encryption';
import { getFormRecipientKeys } from '../features/encryption/api/keysApi';
import { DynamicForm } from '../components/forms/DynamicForm';
import type { Document } from '../types/api.types';
import { DocumentPreviewButton } from '../features/documents';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeKey } = useCryptoKeyStore();
  const { encrypt, decryptFields, isReady: encryptionReady } = useEncryption();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [newComment, setNewComment] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [decryptedVariables, setDecryptedVariables] = useState<Record<string, unknown> | null>(null);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);

  const { data: taskDetail, isLoading, error } = useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: () => taskApi.getTaskDetail(taskId!),
    enabled: !!taskId,
  });

  const { data: documents = [], refetch: refetchDocuments } = useQuery({
    queryKey: ['documents', taskDetail?.task.processInstanceId],
    queryFn: () => documentApi.getDocumentsByProcess(taskDetail!.task.processInstanceId),
    enabled: !!taskDetail?.task.processInstanceId,
  });

  const { data: commentsData, refetch: refetchComments } = useQuery({
    queryKey: ['comments', taskDetail?.task.processInstanceId],
    queryFn: () => commentApi.getCommentsByProcess(taskDetail!.task.processInstanceId, true),
    enabled: !!taskDetail?.task.processInstanceId,
  });

  const claimMutation = useMutation({
    mutationFn: () => taskApi.claimTask(taskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const unclaimMutation = useMutation({
    mutationFn: () => taskApi.unclaimTask(taskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      taskApi.completeTask(taskId!, { variables }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate('/tasks');
    },
  });

  const commentMutation = useMutation({
    mutationFn: (content: string) =>
      commentApi.createComment({
        processInstanceId: taskDetail!.task.processInstanceId,
        taskId: taskId,
        content,
      }),
    onSuccess: () => {
      setNewComment('');
      refetchComments();
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (id: string) => documentApi.deleteDocument(id),
    onSuccess: () => refetchDocuments(),
  });

  // Decrypt variables when task detail is loaded
  useEffect(() => {
    if (!taskDetail || !encryptionReady) return;

    const hasEncryptedData = Object.values(taskDetail.variables).some(
      (value) => isEncryptedProcessVariable(value)
    );

    if (hasEncryptedData) {
      decryptFields(taskDetail.variables)
        .then((decrypted) => {
          setDecryptedVariables(decrypted);
          setDecryptionError(null);
        })
        .catch((error) => {
          console.warn('Could not decrypt variables:', error);
          setDecryptionError('Einige Daten konnten nicht entschlüsselt werden.');
          setDecryptedVariables(taskDetail.variables);
        });
    } else {
      setDecryptedVariables(taskDetail.variables);
    }
  }, [taskDetail, encryptionReady, decryptFields]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !taskDetail) return;

    setIsUploading(true);
    try {
      await documentApi.uploadDocument(
        file,
        taskDetail.task.processInstanceId,
        taskId,
        uploadDescription || undefined
      );
      refetchDocuments();
      setUploadDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const blob = await documentApi.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !taskDetail) {
    return <Alert severity="error">Aufgabe nicht gefunden</Alert>;
  }

  const { task, formDefinition, variables } = taskDetail;
  const isAssignedToMe = task.assignee === activeKey?.keyId;
  const canClaim = !task.assignee;
  const canUnclaim = isAssignedToMe && task.taskAssignment?.allowUnclaim !== false;
  const comments = commentsData?.comments || [];

  // Use decrypted variables or fall back to original
  const displayVariables = decryptedVariables ?? variables;

  const handleSubmit = async (data: Record<string, unknown>) => {
    try {
      // Check if encryption is available and if we should encrypt
      if (encryptionReady && activeKey) {
        // Get recipient keys for this process
        const recipients = await getFormRecipientKeys(task.processDefinitionKey);

        if (recipients.length > 0) {
          // Encrypt all form fields
          const encryptedData = await encrypt(data, recipients);
          completeMutation.mutate({ _encryptedFormData: encryptedData });
          return;
        }
      }

      // Fall back to unencrypted submission if no recipients or encryption not ready
      completeMutation.mutate(data);
    } catch (error) {
      console.error('Encryption failed, submitting unencrypted:', error);
      completeMutation.mutate(data);
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      commentMutation.mutate(newComment);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if there are AI analysis results to display
  const aiVariables = Object.entries(displayVariables).filter(([key]) =>
    key.startsWith('ai') && displayVariables[key]
  );

  // Check if there's any encrypted data that couldn't be decrypted
  const hasEncryptedData = Object.values(displayVariables).some(
    (value) => isEncryptedProcessVariable(value)
  );

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/tasks')}
        sx={{ mb: 2 }}
      >
        Zurueck zur Liste
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {task.taskDisplay?.labels['de'] || task.name}
                </Typography>
                <Chip
                  label={task.taskDisplay?.priority || 'MEDIUM'}
                  color={
                    task.taskDisplay?.priority === 'URGENT'
                      ? 'error'
                      : task.taskDisplay?.priority === 'HIGH'
                        ? 'warning'
                        : 'default'
                  }
                />
              </Box>

              {task.taskDisplay?.descriptions['de'] && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {task.taskDisplay.descriptions['de']}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {canClaim && (
                  <Button
                    variant="contained"
                    startIcon={<PersonIcon />}
                    onClick={() => claimMutation.mutate()}
                    disabled={claimMutation.isPending}
                  >
                    Aufgabe uebernehmen
                  </Button>
                )}
                {canUnclaim && (
                  <Button
                    variant="outlined"
                    onClick={() => unclaimMutation.mutate()}
                    disabled={unclaimMutation.isPending}
                  >
                    Zuweisung aufheben
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* AI Analysis Results (if available) */}
          {aiVariables.length > 0 && (
            <Card sx={{ mb: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                  KI-Analyse Ergebnisse
                </Typography>
                <Box sx={{ '& > div': { mb: 2 } }}>
                  {aiVariables.map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary">
                        {key.replace(/^ai/, '').replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Decryption status */}
          {decryptionError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {decryptionError}
            </Alert>
          )}

          {hasEncryptedData && (
            <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
              Einige Daten sind verschlüsselt und können nur von berechtigten Empfängern gelesen werden.
            </Alert>
          )}

          {formDefinition ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Formular
                </Typography>
                <DynamicForm
                  formDefinition={formDefinition}
                  initialValues={displayVariables}
                  onSubmit={handleSubmit}
                  submitLabel="Aufgabe abschliessen"
                  isSubmitting={completeMutation.isPending}
                />
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Diese Aufgabe hat kein Formular. Sie koennen die Aufgabe direkt abschliessen.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleSubmit({})}
                  disabled={completeMutation.isPending}
                  startIcon={completeMutation.isPending ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  Aufgabe abschliessen
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Document Upload Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileIcon /> Dokumente
              </Typography>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Beschreibung (optional)"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={isUploading}
                  >
                    Datei hochladen
                  </Button>
                </label>
                {isUploading && <LinearProgress sx={{ mt: 1 }} />}
              </Box>

              {documents.length > 0 ? (
                <List dense>
                  {documents.map((doc) => (
                    <ListItem key={doc.id} divider>
                      <ListItemText
                        primary={doc.fileName}
                        secondary={
                          <>
                            {doc.description && <>{doc.description} - </>}
                            {formatFileSize(doc.fileSize)} - {new Date(doc.uploadedAt).toLocaleString('de-DE')}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <DocumentPreviewButton document={doc} />
                        <IconButton
                          edge="end"
                          onClick={() => handleDownload(doc)}
                          title="Herunterladen"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => deleteDocumentMutation.mutate(doc.id)}
                          title="Loeschen"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Dokumente hochgeladen.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CommentIcon /> Kommentare
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Kommentar hinzufuegen..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || commentMutation.isPending}
                >
                  <SendIcon />
                </Button>
              </Box>

              {comments.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {comments.map((comment: Comment) => (
                    <Paper key={comment.id} sx={{ p: 2 }} variant="outlined">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{comment.author}</Typography>
                          {!comment.internal && (
                            <Chip label="Bürger" size="small" color="info" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.createdAt).toLocaleString('de-DE')}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{comment.content}</Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Kommentare.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>

              <Box sx={{ '& > div': { mb: 2 } }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Prozess
                  </Typography>
                  <Typography variant="body2">
                    {task.processDefinitionKey}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Erstellt am
                  </Typography>
                  <Typography variant="body2">
                    {task.createTime
                      ? new Date(task.createTime).toLocaleString('de-DE')
                      : '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Zugewiesen an
                  </Typography>
                  <Typography variant="body2">
                    {task.assignee || 'Nicht zugewiesen'}
                  </Typography>
                </Box>

                {task.candidateGroups.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Kandidatengruppen
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {task.candidateGroups.map((group) => (
                        <Chip key={group} label={group} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {Object.keys(displayVariables).filter(k => !k.startsWith('ai') && !k.startsWith('_encrypted')).length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Prozessvariablen
                </Typography>
                <Box sx={{ '& > div': { mb: 1 } }}>
                  {Object.entries(displayVariables)
                    .filter(([key, value]) =>
                      !['dataConsent', 'truthDeclaration', 'initiator'].includes(key) &&
                      !key.startsWith('ai') &&
                      !key.startsWith('_encrypted') &&
                      !isEncryptedProcessVariable(value)
                    )
                    .map(([key, value]) => (
                      <Box key={key}>
                        <Typography variant="caption" color="text.secondary">
                          {key}
                        </Typography>
                        <Typography variant="body2">
                          {String(value || '-')}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
